/**
 * PROJECT lifecycle: import a GLB, reopen a persisted project after a refresh, autosave the rig
 * state, delete and close.
 *
 * This is the controller that decides which workflow the user lands in (SPEC "GLB Import
 * Pipeline"): a model with a usable skeleton goes to MAP with an automatic mapping proposed, an
 * unrigged model goes to RIG with an estimated skeleton placement.
 *
 * Persistence follows plan D21: the GLB bytes are stored once as a blob, and everything the editor
 * adds (binding, placement, skin weights, clip selection) is stored on the `ProjectRecord`, so a
 * reopen rebuilds the SkinnedMesh from source + placement + weights instead of a re-export.
 */

import { AppError, RIG_VERSION, newId } from '../../shared';
import type { CanonicalBoneId, ProjectRecord, SkinData } from '../../shared';
import { buildBinding } from '../../rig/mappingValidation';
import { importGlbFromArrayBuffer, preflightImport, readFileAsArrayBuffer } from '../../importExport';
import type { ImportedModel, ImportPreflight } from '../../importExport';
import { blobsRepo, ensurePersistentStorage, projectsRepo } from '../../persistence';
import { bindRig } from '../../runtime';
import { analyzeSkeleton } from '../../rig/sourceSkeleton';
import {
  cloneSkinParams,
  defaultSkinParams,
  normalizeSkinParams,
} from '../../rig/autoskin/skinParams';
import type { MappingConfidence } from '../../rig/mapping';
import type { EditorContext } from '../context';
import type { ControllerDeps } from './internal';
import { guard, messageOf } from './internal';
import type { ProjectController } from './types';

function projectNameFor(file: File | Blob, explicit?: string): string {
  const trimmed = explicit?.trim();
  if (trimmed) return trimmed;
  const candidate: unknown = (file as File).name;
  if (typeof candidate === 'string' && candidate.trim()) {
    return candidate.trim().replace(/\.(glb|gltf)$/i, '') || candidate.trim();
  }
  return 'Untitled model';
}

/** Every bone a stored binding names is, by definition, a confirmed mapping. */
function confidenceFromBinding(
  map: Partial<Record<CanonicalBoneId, string>>,
): Partial<Record<CanonicalBoneId, MappingConfidence>> {
  const confidence: Partial<Record<CanonicalBoneId, MappingConfidence>> = {};
  for (const bone of Object.keys(map) as CanonicalBoneId[]) {
    if (map[bone]) confidence[bone] = 'confident';
  }
  return confidence;
}

/**
 * Show what the preflight found. Advisory by decision: a big model is the user's business, and
 * refusing an import they could have completed is worse than letting them watch it be slow.
 */
function reportPreflight(store: EditorContext['store'], preflight: ImportPreflight): void {
  if (preflight.warnings.length === 0) return;
  console.warn('[controllers] import preflight', preflight);
  store.actions.toast(
    'info',
    preflight.warnings.length === 1
      ? preflight.warnings[0]
      : `${preflight.warnings[0]} (+${preflight.warnings.length - 1} more)`,
  );
}

export function createProjectController(deps: ControllerDeps): ProjectController {
  const { ctx } = deps;
  const { store, session, events } = ctx;

  let currentId: string | null = null;
  /**
   * Project saves are read-modify-write over one record, so they must never overlap: two in
   * flight at once and the slower one's older snapshot wins. Every save queues behind the last.
   */
  let saveChain: Promise<unknown> = Promise.resolve();

  /**
   * Whether this session has already asked the browser to make the origin's storage persistent.
   *
   * The flag lives here, not in `storagePersist.ts`, because "once per session" means once per
   * editor session and this controller is built exactly once per session. It also keeps a
   * refresh-simulating test (a fresh controller set over the same IndexedDB) honest: it really
   * does re-run the request, instead of being short-circuited by module state from the previous
   * "page".
   */
  let persistenceRequested = false;

  /**
   * Ask for persistent storage once, and tell the user only if the browser refused.
   *
   * IndexedDB is evictable by default: under disk pressure the browser may throw the whole origin
   * away — projects, GLB blobs, the clip library — silently (audit §2.3 #3). This is the moment
   * to ask, because the user has just saved work and can therefore understand what is being asked
   * about; asking on startup asks about nothing.
   *
   * NOT AWAITED BY THE SAVE CHAIN, on purpose. Firefox answers `persist()` with a doorhanger, and
   * a user who simply ignores it leaves the promise pending forever. Awaited inside
   * `queueProjectWrite`, that would wedge every later project write behind an unanswered prompt —
   * which is precisely the silent data loss this change exists to prevent. The write it follows
   * is already on disk, so nothing here can cost the user the save.
   *
   * The whole body is guarded: this must never be able to turn a save that succeeded into a
   * rejection the user sees as "the project state could not be saved".
   */
  async function requestPersistentStorageOnce(): Promise<void> {
    if (persistenceRequested) return;
    // Set BEFORE the await: a second save must not slip in behind the first one's request.
    persistenceRequested = true;
    try {
      const outcome = await ensurePersistentStorage();
      if (outcome !== 'denied') return;
      console.warn('[controllers] the browser refused persistent storage for this origin');
      /*
       * `error` rather than `info` because `ToastState.kind` has no `warning` (types.ts:191) and
       * this must not read as chatter — the user is the only one who can act on it (in Chrome by
       * installing the app or bookmarking it; in Firefox by accepting the prompt). It is shown
       * once per session by construction, so it cannot become noise, and `ui.toast` being a
       * single slot means it may replace whatever was showing — acceptable for a message about
       * losing everything.
       */
      store.actions.toast(
        'error',
        'This browser would not mark your data as persistent, so it may be deleted when the ' +
          'disk fills up. Export anything you cannot afford to lose.',
      );
    } catch (error) {
      console.warn('[controllers] persistent storage request failed', error);
    }
  }

  /**
   * Queue one write to the project record behind every write already queued.
   *
   * EVERY writer of the record has to go through here, not just `saveProjectState`: each of them
   * is a read-modify-write, so a second writer whose `get` lands between the first one's read and
   * its `put` writes a snapshot that predates it. That is how a freshly stamped `lastOpenedAt`
   * used to be overwritten by an autosave that had read the record just before the stamp.
   *
   * The chain survives a rejected write (the caller still sees its own rejection).
   */
  function queueProjectWrite<T>(run: () => Promise<T>): Promise<T> {
    const next = saveChain.then(run);
    saveChain = next.catch(() => undefined);
    return next;
  }

  /**
   * Everything a project record takes FROM the live session, read in one go.
   *
   * It exists so the read and the write can happen at different moments: the autosave reads the
   * store after the record has been fetched (a snapshot taken before that await is already stale
   * when it is written back — wave-5 finding 11), while `closeProject` has to read it BEFORE the
   * reset wipes it. Same payload, same merge, two capture points.
   */
  interface ProjectStateSnapshot {
    /** The name the toolbar is showing for this project, or `null` to keep the stored one. */
    name: string | null;
    binding: ProjectRecord['binding'];
    placement: ProjectRecord['placement'];
    selectedClipIds: string[];
    skinData: SkinData[];
    skinParams: NonNullable<ProjectRecord['skinParams']>;
  }

  function captureProjectState(id: string): ProjectStateSnapshot {
    const state = store.getState();
    return {
      // The toolbar renames a project through `store.setProject`; without this the new name would
      // live only in memory and be lost on the next reload.
      name: (state.project?.id === id && state.project.name.trim()) || null,
      binding: session.rig?.binding ?? null,
      placement: state.rig.placement,
      selectedClipIds: [...state.library.selectedClipIds],
      skinData: deps.rig.allSkinData(),
      // The parameters are what makes the weights reproducible: without them a reload could
      // re-solve the same cache into different weights.
      skinParams: cloneSkinParams(state.rig.skinParams),
    };
  }

  /** Merge a snapshot onto the record that was just read, and put it back. */
  async function putProjectState(
    record: ProjectRecord,
    snapshot: ProjectStateSnapshot,
  ): Promise<void> {
    const next: ProjectRecord = {
      ...record,
      name: snapshot.name ?? record.name,
      binding: snapshot.binding,
      placement: snapshot.placement,
      selectedClipIds: snapshot.selectedClipIds,
    };
    if (snapshot.skinData.length > 0) next.skinData = snapshot.skinData;
    else delete next.skinData;
    next.skinParams = snapshot.skinParams;

    await projectsRepo.put(next);

    // The first project save that actually reached disk — the one moment at which asking for
    // persistent storage is a question about something the user just did. See
    // `requestPersistentStorageOnce` for why it is not awaited.
    void requestPersistentStorageOnce();
  }

  /**
   * Clear everything in the store that belongs to ONE project — the single reset both directions
   * of a project switch go through (`loadIntoSession` on the way in, `closeProject` on the way
   * out).
   *
   * WHY IT IS ONE FUNCTION AND NOT A LIST OF CALLS AT EACH SITE. The old code reset ten things
   * inline in `loadIntoSession` and four in `closeProject`, and `state.rig.placement` was on
   * neither list. Because `saveProjectState` writes `placement` unconditionally, one project's
   * estimated skeleton was silently persisted onto the next project's record — no undo needed,
   * ticking a clip in EXPORT was enough (audit §2.1 #1, P0). The sibling of that bug had already
   * been found once (see the `selectedClipIds` comment in `loadIntoSession`); a hand-written list
   * is what lets the next field be missed too. So the rig slice is cleared BY CONSTRUCTION
   * (`resetRigState` rebuilds it from `createInitialState`), and anything else project-scoped
   * belongs here rather than at a call site.
   *
   * WHY THE SUPPRESSION IS EXPLICIT. Clearing `placement` is a DATABASE write, not a store write:
   * the subscription in `controllers/index.ts` calls `saveNow()` on every placement change. Run
   * while the outgoing project is still current, this reset writes `placement: null` onto its
   * record and, because `deps.rig.resetInternal()` has emptied `allSkinData()`, makes
   * `saveProjectState` `delete next.skinData` as well — a rigged, skinned project that reopens as
   * an unrigged blob, i.e. a worse P0 than the one being fixed. Two independent guards, neither
   * of which can be defeated by moving a statement: `currentId` is dropped first, so no write can
   * be attributed to the outgoing project at all, and the store writes run inside
   * `suppressPlacementAutosave`, so the subscription does not fire in the first place.
   */
  function resetProjectScopedState(): void {
    // FIRST, and not a formality: `saveProjectState` reads `currentId` to decide whose record a
    // write belongs to. Nothing below may be attributed to the project that is leaving.
    currentId = null;
    deps.suppressPlacementAutosave(() => {
      store.batch(() => {
        // The whole rig slice: placement, the generated flag, the solver parameters, the armed
        // tool, the preview pose, the leak report, the selected joint. `openProject` overwrites
        // the parameters right after this with the ones it restored.
        store.actions.resetRigState();
        // `rigBound` mirrors `session.rig` for the pure mode gate (EXPORT); both callers drop the
        // rig, so EXPORT must stop being reachable here too.
        store.actions.setRigBound(false);
        store.actions.setSelectedClipIds([]);
        /*
         * Path A of the same finding. Undo entries are closures over the document they were
         * recorded against: `moveJoint`'s `revert` in `actions/rig.ts` literally holds the
         * previous project's placement, and Ctrl+Z is live in every mode including MODEL — the
         * screen the project list lives on. One keystroke after a switch wrote project A's
         * skeleton into project B's record.
         *
         * Dispatched as an action rather than calling `ctx.history.clear()` directly so that
         * `state.history.canUndo/canRedo` follow (the store syncs them per dispatch) and the
         * toolbar stops offering an undo that no longer describes anything. `clear()` also drops
         * an open coalesce group, so a joint drag interrupted by a project switch cannot leave
         * the new project merging into the old gesture.
         */
        store.actions.clearHistory();
      });
    });
    // After the store, so the rig controller's `solvedParams` snapshot is taken from the reset
    // parameters instead of the outgoing project's.
    deps.rig.resetInternal();
  }

  /** Everything both `importGlbFile` and `openProject` do once the bytes are decoded. */
  function loadIntoSession(
    buffer: ArrayBuffer,
    model: ImportedModel,
    meta: {
      id: string;
      name: string;
      kind: 'rigged' | 'unrigged';
      /** The record's embed list: `[]` for a fresh import, the stored ticks for a reopen. */
      selectedClipIds: readonly string[];
    },
  ): void {
    // Everything project-scoped goes at once, and `currentId` is null from here until the line
    // below claims the incoming project — see `resetProjectScopedState`.
    resetProjectScopedState();
    session.setSourceGlb(buffer);
    session.setModel(model);
    session.setRig(null);
    session.setBuiltSkeleton(null);
    currentId = meta.id;
    store.actions.setProject({ id: meta.id, name: meta.name, kind: meta.kind, loaded: true });
    /*
     * The embed selection is hydrated HERE, from the record being loaded, not after the mode
     * branches below. Explicit because `libraryController.refresh()` no longer re-reads the record
     * (it raced the save chain), so nothing else clears the previous project's assignment when a
     * new model is imported — and it has to be the incoming ticks rather than `[]`, because the
     * store must never hold an empty selection for a project that has one:
     * `openProject`'s unrigged branch dispatches `setPlacement`, whose subscription saves
     * IMMEDIATELY (controllers/index.ts), and `saveProjectState` writes
     * `state.library.selectedClipIds` straight into the record — which silently wiped the ticks of
     * every tool-rigged project on reopen. `openProject` re-hydrates once more after `refresh()`,
     * filtered against the clips that actually exist.
     */
    store.actions.setSelectedClipIds([...meta.selectedClipIds]);
    events.emit('model-changed');
  }

  function reportWarnings(warnings: readonly string[]): void {
    if (warnings.length === 0) return;
    console.warn('[controllers] import warnings', warnings);
    store.actions.toast(
      'info',
      warnings.length === 1
        ? warnings[0]
        : `${warnings[0]} (+${warnings.length - 1} more import notes)`,
    );
  }

  const controller: ProjectController = {
    async listProjects(): Promise<ProjectRecord[]> {
      return guard(ctx, 'The project list could not be read.', () => projectsRepo.list());
    },

    async importGlbFile(file: File | Blob, name?: string): Promise<void> {
      return guard(ctx, 'The model could not be imported.', async () => {
        // Settle what the OPEN project still owes before anything makes a different one current.
        // See `openProject` for why this is not optional.
        await deps.flushSaves();
        const buffer = await readFileAsArrayBuffer(file);
        // Import BEFORE anything is written: a bad GLB must not leave a project behind.
        const model = await importGlbFromArrayBuffer(buffer);

        reportPreflight(store, await preflightImport(buffer, model));

        // One transaction for the blob and the record together: a failed project write must not
        // leave an unreferenced multi-megabyte GLB behind (audit P1).
        const now = Date.now();
        const record = await projectsRepo.createWithBlob(
          new Blob([buffer]),
          (blobId): ProjectRecord => ({
            id: newId(),
            name: projectNameFor(file, name),
            createdAt: now,
            updatedAt: now,
            kind: model.kind,
            sourceGlbBlobId: blobId,
            binding: null,
            placement: null,
            selectedClipIds: [],
          }),
        );

        loadIntoSession(buffer, model, record);
        reportWarnings(model.warnings);

        if (model.kind === 'rigged') {
          store.actions.setMode('map');
          deps.controllers.mapping.autoMap();
        } else {
          deps.controllers.rig.estimatePlacement();
          store.actions.setMode('rig');
        }

        await deps.controllers.library.refresh();
      });
    },

    async openProject(id: string): Promise<void> {
      return guard(ctx, 'The project could not be opened.', async () => {
        /*
         * Path C of audit §2.1 #1. A joint drag debounces its save for 300 ms, and nothing used
         * to flush it before a different project became current: the timer fired afterwards and
         * `saveProjectState` read "the current project" at that moment, writing the outgoing
         * project's state onto the incoming record. The scope check inside `saveProjectState` now
         * refuses that write — but refusing it would simply LOSE the user's last drag, so the
         * pending save is settled here first, while the project it belongs to is still open.
         * `modelPanel` calls `openProject` directly, so it has to happen inside this method.
         */
        await deps.flushSaves();
        /*
         * Audit §2.3 #2. `normalizeProjectForRead` repairs what it can on the way in — a corrupt
         * binding becomes `null`, unreadable weights are dropped — and every reader of the record
         * threw the resulting `problems` away, `projectsRepo.get` included. The repaired version
         * then became what is ON DISK at the first ordinary edit, so a user could lose their bone
         * mapping without a single line of UI ever mentioning it.
         *
         * Read here, BEFORE `get`, and reported at the end of the open (below). Before, because
         * the open itself can commit the repairs: the unrigged branch dispatches `setPlacement`,
         * whose subscription saves immediately, and that save is a read-modify-write of the
         * repaired record — asked afterwards, the question answers "nothing wrong" about a record
         * that just lost the mapping. This is the caller `readProblems` was written for and never
         * had (its own comment said "so the UI can warn"; nothing outside `src/persistence` ever
         * called it).
         */
        const repairs = await projectsRepo.readProblems(id);
        const record = await projectsRepo.get(id);
        if (!record) {
          throw new AppError('PROJECT_NOT_FOUND', `No project with id "${id}".`, { id });
        }
        const blob = await blobsRepo.get(record.sourceGlbBlobId);
        if (!blob) {
          throw new AppError(
            'PROJECT_BLOB_MISSING',
            `The stored GLB for project "${record.name}" is gone; re-import the model.`,
            { id, blobId: record.sourceGlbBlobId },
          );
        }

        const buffer = await blob.arrayBuffer();
        const model = await importGlbFromArrayBuffer(buffer);
        loadIntoSession(buffer, model, record);

        if (record.kind === 'rigged') {
          const saved = record.binding;
          if (saved) {
            const analysis = analyzeSkeleton(model.root);

            // A binding saved before `humanoid-v2` has no rest alignment, so its clips were
            // authored relative to this rig's own rest rather than the canonical T-pose. The
            // binding itself is fully derivable from the model, so rebuild it and say plainly
            // that older clips may sit differently — silently keeping the old one would split
            // the library into two conventions that look identical on disk.
            const staleVersion = saved.rigVersion !== RIG_VERSION ? saved.rigVersion : null;
            const binding = staleVersion
              ? buildBinding(saved.canonicalToSourceBone, analysis.bones, saved.worldCorrection)
              : saved;

            /*
             * A STORED BINDING THAT NO LONGER RESOLVES MUST NOT MAKE THE PROJECT UNOPENABLE.
             *
             * `bindRig` throws on a binding that maps no bone of this model
             * (`BIND_NO_BONES_MAPPED`) or names bones the model does not have (`BIND_FAILED`).
             * That throw is right for the SDK — an integrator whose `autoBind` produced a rig
             * that drives nothing had no other signal (audit §2.5 #5) — and it stays as it is.
             *
             * On THIS path it is wrong to let it escape. Both states are reachable from disk:
             * `assertBinding` accepts an empty `canonicalToSourceBone` (`persistence/validate.ts`),
             * and the stale-`rigVersion` branch above rebuilds the binding from `analysis.bones`,
             * which drops every name this model lacks. Escaping, the error aborts `openProject`
             * altogether, so the project cannot be opened AT ALL — while the message tells the
             * user to "map its bones by hand", which is exactly what the aborted open denied them.
             * The model is fixed by the record; the mapping is the only repairable half.
             *
             * So the failure degrades to what this path did before the throw existed: MAP mode,
             * no rig, the stored draft on screen (`Auto-map` in the panel is one click away), and
             * a named reason instead of a dead end.
             */
            let bindError: unknown = null;
            try {
              const rig = bindRig(model.root, binding);
              session.setRig(rig);
              // `rigBound` mirrors `session.rig` for the pure mode gate (EXPORT). This path emits
              // no `'rig-changed'`, so the flag is the only thing that tells the toolbar the rig
              // is back after a reload. It stays false on the failure path: there is no rig, and
              // EXPORT must not be reachable.
              store.actions.setRigBound(true);
            } catch (error) {
              bindError = error;
              console.warn('[controllers] stored binding could not be bound', { id, error });
            }
            store.batch(() => {
              store.actions.setSourceBones(analysis.bones.map((b) => b.name));
              store.actions.setMappingDraft(
                { ...binding.canonicalToSourceBone },
                confidenceFromBinding(binding.canonicalToSourceBone),
              );
              store.actions.setWorldCorrection(binding.worldCorrection);
              if (bindError) {
                store.actions.toast(
                  'error',
                  `This project's saved bone mapping could not be applied to its model: ` +
                    `${messageOf(bindError, 'The rig could not be bound.')} The project is open in ` +
                    'MAP so you can map the bones again.',
                );
              } else if (staleVersion) {
                store.actions.toast(
                  'info',
                  `This project was rigged for "${staleVersion}"; its rig was re-bound for ` +
                    `"${RIG_VERSION}". Animations authored before the upgrade may sit ` +
                    'differently and are worth re-checking.',
                );
              }
            });
            deps.controllers.mapping.validate();
          } else {
            deps.controllers.mapping.autoMap();
          }
          store.actions.setMode('map');
        } else {
          if (record.placement) store.actions.setPlacement(record.placement);
          // BEFORE the rebuild: the rig controller snapshots the parameters the restored weights
          // correspond to, and a later slider move re-solves from exactly those.
          store.actions.setSkinParams(
            record.skinParams ? normalizeSkinParams(record.skinParams) : defaultSkinParams(),
          );
          const skinData: SkinData[] | undefined = record.skinData;
          if (record.placement && skinData && skinData.length > 0) {
            deps.rig.rebuildFromPersisted(record.placement, skinData);
          }
          store.actions.setMode('rig');
        }

        await deps.controllers.library.refresh();
        /*
         * Re-hydrate now that the library list is known, keeping only ids that still have a clip.
         * A stale id is otherwise invisible — no row to untick — while `EXPORT_CLIP_MISSING` keeps
         * refusing every export, leaving the user no way out. The cleaned list is NOT written back
         * eagerly; the next `saveProjectState` (any project edit, or the close) persists it, and
         * re-filtering on each open is idempotent in the meantime.
         */
        const knownClipIds = new Set(store.getState().library.clips.map((clip) => clip.id));
        store.actions.setSelectedClipIds(
          record.selectedClipIds.filter((clipId) => knownClipIds.has(clipId)),
        );

        // "Last opened" is metadata for the project list, and the open has already succeeded by
        // here. Fire-and-forget with the rejection swallowed on purpose: `restoreLastSession`
        // deletes the stored last-project key on ANY rejection from `openProject`, so a failed
        // timestamp write must not cost the user their session restore. Queued on the same chain
        // as the saves so an autosave started around the open cannot write over the stamp with a
        // record it read before it.
        void queueProjectWrite(() => projectsRepo.markOpened(id)).catch(() => undefined);

        /*
         * LAST, and after the open has succeeded. `ui.toast` is a single slot
         * (`actions/context.ts:72`), and this open may already have toasted — the stale
         * `rigVersion` note, or an import warning — so the damage report has to be the one that
         * survives; it is the only one that describes data the user has lost.
         *
         * The project is NOT blocked from saving. Blocking was considered and rejected: it would
         * leave the user with a project they can look at and never work on. What changes is that
         * the loss is named ("the bone mapping was corrupt and was dropped — re-confirm the
         * mapping") at the moment they can still act on it, instead of a `console.warn` nobody
         * reads.
         */
        if (repairs.length > 0) {
          console.warn('[controllers] project opened with read-time repairs', { id, repairs });
          store.actions.toast(
            'error',
            `Some of this project's stored data was unreadable and was repaired: ` +
              `${repairs.join('; ')}. Saving from here on keeps the repaired version.`,
          );
        }
      });
    },

    async glbSizes(projects: readonly ProjectRecord[]): Promise<Record<string, number>> {
      return guard(ctx, 'The stored model sizes could not be read.', async () => {
        const sizes: Record<string, number> = {};
        await Promise.all(
          projects.map(async (project) => {
            // Missing is normal, not an error: a blob can be gone while the record survives, and
            // the list still has to render (that project simply shows no size).
            const size = await blobsRepo.sizeOf(project.sourceGlbBlobId);
            if (typeof size === 'number') sizes[project.id] = size;
          }),
        );
        return sizes;
      });
    },

    async deleteProject(id: string): Promise<void> {
      return guard(ctx, 'The project could not be deleted.', async () => {
        await projectsRepo.remove(id);
        if (currentId === id) controller.closeProject();
        store.actions.toast('info', 'Project deleted.');
      });
    },

    closeProject(): void {
      /*
       * THE THIRD EXIT, and the one that used to drop the last edit on the floor.
       *
       * `importGlbFile` and `openProject` both `await deps.flushSaves()` before anything makes a
       * different project current (audit §2.1 #1 Path C); closing had nothing. So a debounced save
       * still owed — `rigController.resolveOnce` schedules one after every skin re-solve — fired
       * AFTER `resetProjectScopedState()` had already nulled `currentId`, and `saveProjectState`
       * dropped it at `if (!id) return`. Drag a skin blend slider, hit "Close project" inside
       * 300 ms, and that solve's `skinParams` were silently never written.
       *
       * It is NOT fixed by flushing here, awaited or not. `closeProject` is synchronous — its
       * contract is pinned in `controllers/types.ts` and `modelPanel` and `deleteProject` both
       * rely on the close having happened by the time the call returns — and a flush that is
       * merely started cannot help either: `saveProjectState` reads the store AFTER its `await`,
       * so the write it performs would carry the state the reset had already cleared, i.e.
       * `placement: null` and a deleted `skinData` on a rigged project. That is the exact P0 the
       * suppression in `resetProjectScopedState` exists to prevent.
       *
       * So the owed state is CAPTURED here, while it is still intact, and written against the
       * project it belongs to by id. Only when a save is actually outstanding: `projectsRepo.put`
       * stamps `updatedAt` and the project list is sorted by it, so writing unconditionally would
       * reorder the list for merely closing a project (the same reason `markOpened` bypasses
       * `put`).
       */
      const closing = currentId;
      const owed =
        closing && deps.controllers.hasPendingSaves() ? captureProjectState(closing) : null;

      /*
       * The same reset as the switch-in path (delete-current-project routes through here too).
       * It covers what this function used to spell out inline — `rigBound`, `resetInternal`, the
       * skin state that otherwise opened the next project with the previous one's parameters and
       * a stale "generated" flag (F12) — plus the placement and the undo stack that were missing
       * from that list (audit §2.1 #1).
       */
      resetProjectScopedState();
      session.clear();
      store.actions.setProject(null);
      events.emit('model-changed');

      if (closing && owed) {
        // Queued like every other record writer, so it cannot interleave with a save that is
        // already in flight for the same record (`queueProjectWrite`). Fire-and-forget: the close
        // itself has happened, `guard` has already told the user if the write failed, and the
        // rejection must not become an unhandled one on a `void` method.
        void queueProjectWrite(() =>
          guard(ctx, 'The project state could not be saved.', async () => {
            const record = await projectsRepo.get(closing);
            // Gone underneath us — `deleteProject` removes the record and then routes through
            // here, so this is the normal case, not an error. Nothing to merge into, and the
            // deleted project must never be resurrected by its own close.
            if (!record) return;
            await putProjectState(record, owed);
          }),
        ).catch(() => undefined);
      }
    },

    async saveProjectState(expectedProjectId?: string | null): Promise<void> {
      const id = currentId;
      if (!id) return;
      /*
       * The scope check, and it happens BEFORE the first await on purpose. The existing
       * `currentId !== id` guard further down cannot help here: by the time it runs, this save
       * has already decided it belongs to whatever project was current when it fired.
       * `expectedProjectId` is the project the caller had in mind when it decided to save: the
       * debounced scheduler captures it at schedule time, so a save queued for project A that
       * fires after project B was opened drops itself instead of writing A's store state onto
       * B's record (audit §2.1 #1, Path C). Direct callers pass nothing — they are current by
       * definition.
       */
      if (expectedProjectId !== undefined && expectedProjectId !== id) return;
      const run = queueProjectWrite(() =>
        guard(ctx, 'The project state could not be saved.', async () => {
          const record = await projectsRepo.get(id);
          if (!record) return; // deleted underneath us; nothing to merge into
          // A different project may have been opened while we waited; its state must not be
          // written onto this record.
          if (currentId !== id) return;
          // Read the store AFTER the await, never before it. A snapshot taken before the (async)
          // record read is already stale by the time it is written back, and it silently reverted
          // whatever happened in between — the reported "selected clips are empty after a
          // reload" (wave-5 review finding 11). `closeProject` is the one caller that must do the
          // opposite, and it says why there.
          await putProjectState(record, captureProjectState(id));
        }),
      );
      // `queueProjectWrite` already keeps the chain alive past a rejection; the caller still sees
      // this save's own rejection.
      return run;
    },

    currentProjectId(): string | null {
      return currentId;
    },
  };

  return controller;
}
